import { useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { Information as InformationIcon } from '@mint-lib/icons';
import {
  Label,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@mint-lib/tw-components';
import {
  ComboboxInput,
  ComboboxInputRoot,
  ComboboxItem,
  ComboboxMenu,
} from '@mint-lib/tw-components/patterns';
import { cn } from '@mint-lib/tw-utils';

type Props = {
  initialValue: string;
  label: string;
  infoText?: string;
  options: { value: string; label: string }[];
  onChange: (value: string | undefined) => void;
  placeholder?: string;
  withSearch?: boolean;
};

const Select = ({
  initialValue,
  label,
  infoText,
  options,
  placeholder,
  onChange,
  withSearch = false,
}: Props) => {
  const { t } = useTranslation('@mint-lib/ai-chat');

  const [value, setValue] = useState<string | undefined>(initialValue);
  const [isOpen, setIsOpen] = useState(false);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
  };

  const handleSelect = (value: string | undefined) => {
    setValue(value);
    onChange(value);
    setIsOpen(false);
  };

  const inputValue = options.find((o) => o.value === value)?.label;

  return (
    <div className="flex flex-col gap-4">
      <ComboboxInputRoot>
        <ComboboxLabel infoText={infoText} label={label} />
        <ComboboxInput
          value={inputValue}
          placeholder={placeholder || t('Select...')}
          open={isOpen}
          onOpenChange={handleOpenChange}
        >
          <ComboboxMenu withSearch={withSearch}>
            {options.map((option) => (
              <ComboboxItem
                key={option.value}
                value={option.value}
                onSelect={handleSelect}
                data-state={value === option.value ? 'checked' : 'unchecked'}
              >
                <Label htmlFor={option.value}>{option.label}</Label>
              </ComboboxItem>
            ))}
          </ComboboxMenu>
        </ComboboxInput>
      </ComboboxInputRoot>
    </div>
  );
};

export { Select };

type HelperTextProps = {
  label: string;
  infoText?: string;
  isDisabled?: false;
};

const ComboboxLabel = ({ label, infoText, isDisabled }: HelperTextProps) => {
  const [container, setContainer] = useState<HTMLDivElement | null>(null);

  return (
    <div className="mb-1 flex flex-row gap-1" ref={setContainer}>
      <Label
        variant="label01"
        color={'secondary'}
        disabled={isDisabled}
        data-disabled={isDisabled}
      >
        {label}
      </Label>
      {infoText && (
        <Tooltip>
          <TooltipTrigger asChild>
            <InformationIcon
              aria-label="information-icon"
              className={cn(
                'fill-icon-secondary',
                isDisabled && 'fill-text-disabled',
              )}
            />
          </TooltipTrigger>
          <TooltipContent side="top" align="center" container={container}>
            {infoText}
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
};
